import { useState } from 'react';
import { CreditCard, Copy, Check, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';

// Luhn algorithm to generate valid check digit
const calculateLuhnCheckDigit = (partialNumber: string): string => {
  const digits = partialNumber.split('').reverse().map(Number);
  let sum = 0;
  
  for (let i = 0; i < digits.length; i++) {
    let digit = digits[i];
    if (i % 2 === 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  
  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit.toString();
};

// Generate random digits
const generateRandomDigits = (length: number): string => {
  let result = '';
  for (let i = 0; i < length; i++) {
    result += Math.floor(Math.random() * 10).toString();
  }
  return result;
};

// Generate expiry date
const generateExpiry = (format: string): string => {
  const currentYear = new Date().getFullYear();
  const month = String(Math.floor(Math.random() * 12) + 1).padStart(2, '0');
  const year = currentYear + Math.floor(Math.random() * 5) + 1;
  
  if (format === 'MM/YY') {
    return `${month}/${String(year).slice(-2)}`;
  } else if (format === 'MM/YYYY') {
    return `${month}/${year}`;
  } else {
    return `${month}|${String(year).slice(-2)}`;
  }
};

// Generate CVV
const generateCVV = (length: number = 3): string => {
  return generateRandomDigits(length);
};

const BinGenerator = () => {
  const [bin, setBin] = useState('');
  const [quantity, setQuantity] = useState([10]);
  const [expiryFormat, setExpiryFormat] = useState('MM/YY');
  const [generatedCards, setGeneratedCards] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  const generateCards = () => {
    if (!bin || bin.length < 6) {
      toast.error('Please enter a valid BIN (minimum 6 digits)');
      return;
    }

    setIsLoading(true);
    
    setTimeout(() => {
      const cards: string[] = [];
      const cleanBin = bin.replace(/\D/g, '').slice(0, 8);
      const isAmex = cleanBin.startsWith('34') || cleanBin.startsWith('37');
      const cardLength = isAmex ? 15 : 16;
      const cvvLength = isAmex ? 4 : 3;
      
      for (let i = 0; i < quantity[0]; i++) {
        // Generate remaining digits (excluding check digit)
        const remainingLength = cardLength - cleanBin.length - 1;
        const randomPart = generateRandomDigits(remainingLength);
        const partialCard = cleanBin + randomPart;
        
        // Calculate and append check digit using Luhn
        const checkDigit = calculateLuhnCheckDigit(partialCard);
        const fullCardNumber = partialCard + checkDigit;
        
        // Generate expiry and CVV
        const expiry = generateExpiry(expiryFormat);
        const cvv = generateCVV(cvvLength);
        
        cards.push(`${fullCardNumber}|${expiry}|${cvv}`);
      }
      
      setGeneratedCards(cards);
      setIsLoading(false);
      toast.success(`Generated ${quantity[0]} cards`);
    }, 500);
  };

  const copyCard = (card: string, index: number) => {
    navigator.clipboard.writeText(card);
    setCopiedIndex(index);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const copyAll = () => {
    navigator.clipboard.writeText(generatedCards.join('\n'));
    setCopiedAll(true);
    toast.success('All cards copied to clipboard');
    setTimeout(() => setCopiedAll(false), 2000);
  };

  return (
    <Card className="glass border-border/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary" />
          BIN Generator
        </CardTitle>
        <CardDescription>
          Generate card numbers using Luhn algorithm for testing purposes
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="bin">BIN Number (First 6-8 digits)</Label>
            <Input
              id="bin"
              placeholder="e.g., 424242"
              value={bin}
              onChange={(e) => setBin(e.target.value.replace(/\D/g, '').slice(0, 8))}
              maxLength={8}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="expiry-format">Expiry Format</Label>
            <Select value={expiryFormat} onValueChange={setExpiryFormat}>
              <SelectTrigger id="expiry-format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MM/YY">MM/YY</SelectItem>
                <SelectItem value="MM/YYYY">MM/YYYY</SelectItem>
                <SelectItem value="MM|YY">MM|YY</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Quantity: {quantity[0]}</Label>
          <Slider
            value={quantity}
            onValueChange={setQuantity}
            min={1}
            max={50}
            step={1}
            className="w-full"
          />
        </div>

        <Button onClick={generateCards} disabled={isLoading} className="w-full gap-2">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Generate Cards
        </Button>

        {generatedCards.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Generated Cards ({generatedCards.length})</Label>
              <Button variant="outline" size="sm" onClick={copyAll} className="gap-2">
                {copiedAll ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                Copy All
              </Button>
            </div>
            <ScrollArea className="h-[300px] rounded-lg border bg-muted/30 p-3">
              <div className="space-y-2">
                {generatedCards.map((card, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 rounded bg-background/50 font-mono text-sm hover:bg-background/80 transition-colors"
                  >
                    <span className="truncate flex-1">{card}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 flex-shrink-0"
                      onClick={() => copyCard(card, index)}
                    >
                      {copiedIndex === index ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BinGenerator;
